import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { useAccountContext } from "../../contexts/Account";
import { retryWithBackoff } from "../../utils/OfflineDrain";
import {
  clearDemoPayload,
  loadDemoPayload,
  saveDemoPayload,
  seedDemoPayload,
} from "./ListsDemoData";
import {
  drainListsQueue,
  ensureListsDrainTriggers,
  isQueueableError,
  teardownListsDrainTriggers,
} from "./ListsOfflineDrain";
import {
  PendingListOp,
  cacheRead,
  cacheWrite,
  queueCount,
  queueOps,
} from "./ListsOfflineQueue";
import {
  DeleteList,
  DeleteListItem,
  DeleteListItems,
  GetLists,
  PostList,
  PostListItem,
  describeListsError,
  isListsApiConfigured,
} from "./ListsCrud";
import {
  ListItem,
  ListItemEdit,
  ListMeta,
  ListsLoadState,
  ListsPayload,
} from "./types";

const SELECTED_LIST_STORAGE_KEY = "ListsSelectedListId";
const SORT_ORDER_STEP = 1000;

type ItemsByList = Record<string, ListItem[]>;

interface ListsContextValue {
  lists: ListMeta[];
  itemsByList: ItemsByList;
  selectedListId: string | null;
  loadState: ListsLoadState;
  errorMessage: string | null;
  /** False when REACT_APP_AWS_LISTS_API_URL is unset — the page shows a neutral notice. */
  apiConfigured: boolean;
  /** True when the API is unconfigured and the page is running on local sample data. */
  demoMode: boolean;
  /** Demo mode only: throw away local edits and re-seed the sample lists. */
  resetDemoData: () => void;
  /** Changes saved locally and still waiting for a connection. */
  pendingCount: number;
  /** Set briefly after a drain so the page can confirm what synced, then cleared. */
  syncedCount: number | null;
  clearSyncedCount: () => void;
  /** True when the list on screen came from the local cache because the fetch failed. */
  usingCachedData: boolean;
  clearError: () => void;
  loadLists: (force?: boolean) => Promise<void>;
  selectList: (listId: string) => void;
  createList: (name: string) => Promise<void>;
  renameList: (listId: string, name: string) => Promise<void>;
  removeList: (listId: string) => Promise<void>;
  addItem: (
    listId: string,
    text: string,
    quantity?: string,
    note?: string
  ) => Promise<void>;
  toggleItem: (listId: string, itemId: string) => Promise<void>;
  updateItem: (
    listId: string,
    itemId: string,
    edit: ListItemEdit
  ) => Promise<void>;
  removeItem: (listId: string, itemId: string) => Promise<void>;
  deleteChecked: (listId: string) => Promise<void>;
  /** Checks or unchecks every item in the list at once. */
  setAllChecked: (listId: string, checked: boolean) => Promise<void>;
  /** Commits a drag-reorder. Takes the list's item ids in their new display order. */
  reorderItems: (listId: string, orderedItemIds: string[]) => Promise<void>;
}

const ListsContext = createContext<ListsContextValue | null>(null);

// Unchecked first, then checked, each group by SortOrder.
function sortListItems(items: ListItem[]): ListItem[] {
  return [...items].sort((a, b) => {
    if (a.Checked !== b.Checked) {
      return a.Checked ? 1 : -1;
    }
    return a.SortOrder - b.SortOrder;
  });
}

function nextSortOrder(existing: { SortOrder: number }[]): number {
  if (existing.length === 0) {
    return SORT_ORDER_STEP;
  }
  const max = existing.reduce(
    (highest, row) => Math.max(highest, row.SortOrder ?? 0),
    0
  );
  return max + SORT_ORDER_STEP;
}

function readStoredSelectedListId(): string | null {
  try {
    return window.localStorage.getItem(SELECTED_LIST_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredSelectedListId(listId: string | null) {
  try {
    if (listId == null) {
      window.localStorage.removeItem(SELECTED_LIST_STORAGE_KEY);
    } else {
      window.localStorage.setItem(SELECTED_LIST_STORAGE_KEY, listId);
    }
  } catch {
    // Storage unavailable — remembering the selection is a convenience, not a requirement.
  }
}

const Lists = (props: { children?: React.ReactNode }) => {
  const { getRefreshedToken, dataUserId, impersonatedUserId } =
    useAccountContext();

  const [lists, setLists] = useState<ListMeta[]>([]);
  const [itemsByList, setItemsByList] = useState<ItemsByList>({});
  const [selectedListId, setSelectedListId] = useState<string | null>(
    readStoredSelectedListId()
  );
  const [loadState, setLoadState] = useState<ListsLoadState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncedCount, setSyncedCount] = useState<number | null>(null);
  const [usingCachedData, setUsingCachedData] = useState(false);

  // Plain mirror of the data, updated every render, so an action can snapshot the
  // current state before mutating it and restore that snapshot if the write fails.
  const stateRef = useRef({ lists, itemsByList, selectedListId });
  stateRef.current = { lists, itemsByList, selectedListId };

  // Synchronous guard so a double mount (React StrictMode) or a quick
  // navigate-away-and-back cannot fire a second fetch.
  const loadStateRef = useRef<ListsLoadState>("idle");

  const apiConfigured = isListsApiConfigured();
  // With no endpoint to talk to, run on local sample data instead of showing a dead page.
  const demoMode = !apiConfigured;

  const applyLoadState = useCallback((next: ListsLoadState) => {
    loadStateRef.current = next;
    setLoadState(next);
  }, []);

  const clearError = useCallback(() => setErrorMessage(null), []);

  const restore = useCallback(
    (snapshot: {
      lists: ListMeta[];
      itemsByList: ItemsByList;
      selectedListId: string | null;
    }) => {
      stateRef.current = snapshot;
      setLists(snapshot.lists);
      setItemsByList(snapshot.itemsByList);
      setSelectedListId(snapshot.selectedListId);
      writeStoredSelectedListId(snapshot.selectedListId);
    },
    []
  );

  const clearSyncedCount = useCallback(() => setSyncedCount(null), []);

  // Starts the background drain. Idempotent, and only ever called once something is
  // actually queued, so a user who never loses signal never pays for the interval.
  const startDraining = useCallback(() => {
    ensureListsDrainTriggers({
      onCountChange: setPendingCount,
      onSynced: setSyncedCount,
    });
  }, []);

  // Optimistic write. Applies locally, calls the API, and on failure either keeps the
  // change and queues it for later, or reverts and reports it.
  const write = useCallback(
    async (
      apply: () => void,
      call: () => Promise<unknown>,
      ops?: PendingListOp[]
    ) => {
      const snapshot = stateRef.current;
      setErrorMessage(null);
      apply();
      if (demoMode) {
        return; // Nothing to call; the persistence effect below stores the result.
      }
      try {
        // A slow connection often succeeds on the second try, which is cheaper than a
        // round trip through the queue. Only retried for failures worth retrying.
        await retryWithBackoff(call, [1500, 3000], isQueueableError);
      } catch (error) {
        // The offline path: keep what the user did, write it to IndexedDB, and let the
        // drain replay it. Disabled during impersonation for the same reason the
        // inventory queue disables it, since queued work outlives the session.
        const queueable =
          ops != null &&
          ops.length > 0 &&
          impersonatedUserId == null &&
          isQueueableError(error);

        if (queueable) {
          try {
            await queueOps(ops, dataUserId);
            setPendingCount(await queueCount());
            startDraining();
            return; // Optimistic state stands. The change is not lost.
          } catch (queueError) {
            console.error("Failed to queue list change for later:", queueError);
          }
        }

        restore(snapshot);
        setErrorMessage(describeListsError(error));
      }
    },
    [dataUserId, demoMode, impersonatedUserId, restore, startDraining]
  );

  const selectList = useCallback((listId: string) => {
    setSelectedListId(listId);
    writeStoredSelectedListId(listId);
  }, []);

  // Takes a payload from either the API or the demo seed and puts it into state.
  const applyPayload = useCallback((payload: ListsPayload) => {
    const grouped: ItemsByList = {};
    payload.lists.forEach((list) => {
      grouped[list.ListId] = [];
    });
    payload.items.forEach((item) => {
      if (grouped[item.ListId] == null) {
        grouped[item.ListId] = [];
      }
      grouped[item.ListId].push(item);
    });
    Object.keys(grouped).forEach((listId) => {
      grouped[listId] = sortListItems(grouped[listId]);
    });

    const sortedLists = [...payload.lists].sort(
      (a, b) => a.SortOrder - b.SortOrder
    );

    setLists(sortedLists);
    setItemsByList(grouped);

    // Keep the remembered selection only if that list still exists.
    const stored = readStoredSelectedListId();
    const valid =
      stored != null && sortedLists.some((list) => list.ListId === stored);
    const nextSelected = valid
      ? stored
      : sortedLists.length > 0
      ? sortedLists[0].ListId
      : null;
    setSelectedListId(nextSelected);
    writeStoredSelectedListId(nextSelected);
  }, []);

  const loadLists = useCallback(
    async (force: boolean = false) => {
      if (loadStateRef.current === "loading") {
        return;
      }
      if (!force && loadStateRef.current === "ready") {
        return;
      }

      if (demoMode) {
        applyLoadState("loading");
        setErrorMessage(null);
        applyPayload(loadDemoPayload());
        applyLoadState("ready");
        return;
      }

      if (dataUserId == null) {
        return;
      }

      applyLoadState("loading");
      setErrorMessage(null);

      // Replay anything queued before reading, so the server's answer already includes
      // offline work and cannot come back and overwrite it.
      const queued = await queueCount();
      setPendingCount(queued);
      if (queued > 0) {
        startDraining();
        await drainListsQueue();
        setPendingCount(await queueCount());
      }

      try {
        applyPayload(await GetLists(getRefreshedToken, dataUserId));
        setUsingCachedData(false);
        applyLoadState("ready");
      } catch (error) {
        // No signal is the case this feature has to survive, so fall back to the copy
        // saved on the last successful visit. It already contains any queued edits,
        // because the cache is rewritten on every change.
        if (isQueueableError(error)) {
          try {
            const cached = await cacheRead(dataUserId);
            if (cached != null) {
              applyPayload(cached);
              setUsingCachedData(true);
              applyLoadState("ready");
              return;
            }
          } catch (cacheError) {
            console.error("Failed to read the cached lists:", cacheError);
          }
        }
        applyLoadState("error");
        setErrorMessage(describeListsError(error));
      }
    },
    [
      applyLoadState,
      applyPayload,
      dataUserId,
      demoMode,
      getRefreshedToken,
      startDraining,
    ]
  );

  const resetDemoData = useCallback(() => {
    if (!demoMode) {
      return;
    }
    clearDemoPayload();
    setErrorMessage(null);
    writeStoredSelectedListId(null);
    applyPayload(seedDemoPayload());
  }, [applyPayload, demoMode]);

  // Demo edits persist to localStorage so a refresh keeps whatever you clicked through.
  useEffect(() => {
    if (!demoMode || loadState !== "ready") {
      return;
    }
    const items = Object.keys(itemsByList).reduce<ListItem[]>(
      (all, listId) => all.concat(itemsByList[listId]),
      []
    );
    saveDemoPayload({ lists, items });
  }, [demoMode, itemsByList, lists, loadState]);

  // Keep the offline copy current. Written on every change rather than only on load, so
  // it already holds whatever is still sitting in the queue and a reload with no signal
  // shows the list exactly as the user left it.
  useEffect(() => {
    if (demoMode || loadState !== "ready" || dataUserId == null) {
      return;
    }
    const items = Object.keys(itemsByList).reduce<ListItem[]>(
      (all, listId) => all.concat(itemsByList[listId]),
      []
    );
    cacheWrite(dataUserId, { lists, items }).catch((error) => {
      console.error("Failed to cache lists for offline use:", error);
    });
  }, [dataUserId, demoMode, itemsByList, lists, loadState]);

  // Pick up work queued in a previous session, and tear the triggers down on unmount.
  useEffect(() => {
    if (demoMode) {
      return;
    }
    let cancelled = false;
    queueCount()
      .then((count) => {
        if (cancelled || count === 0) {
          return;
        }
        setPendingCount(count);
        startDraining();
        drainListsQueue();
      })
      .catch(() => {
        // An unreadable queue must not stop the page from working.
      });
    return () => {
      cancelled = true;
      teardownListsDrainTriggers();
    };
  }, [demoMode, startDraining]);

  const createList = useCallback(
    async (name: string) => {
      const trimmed = name.trim();
      if (trimmed === "") {
        return;
      }
      const now = new Date().toISOString();
      const list: ListMeta = {
        ListId: uuidv4(),
        Name: trimmed,
        SortOrder: nextSortOrder(stateRef.current.lists),
        CreatedAt: now,
        UpdatedAt: now,
      };
      await write(
        () => {
          setLists((prev) => [...prev, list]);
          setItemsByList((prev) => ({ ...prev, [list.ListId]: [] }));
          setSelectedListId(list.ListId);
          writeStoredSelectedListId(list.ListId);
        },
        () => PostList(list, getRefreshedToken, dataUserId),
        [{ kind: "list.upsert", list }]
      );
    },
    [dataUserId, getRefreshedToken, write]
  );

  const renameList = useCallback(
    async (listId: string, name: string) => {
      const trimmed = name.trim();
      const existing = stateRef.current.lists.find(
        (list) => list.ListId === listId
      );
      if (existing == null || trimmed === "" || trimmed === existing.Name) {
        return;
      }
      const updated: ListMeta = {
        ...existing,
        Name: trimmed,
        UpdatedAt: new Date().toISOString(),
      };
      await write(
        () => {
          setLists((prev) =>
            prev.map((list) => (list.ListId === listId ? updated : list))
          );
        },
        () => PostList(updated, getRefreshedToken, dataUserId),
        [{ kind: "list.upsert", list: updated }]
      );
    },
    [dataUserId, getRefreshedToken, write]
  );

  const removeList = useCallback(
    async (listId: string) => {
      const remaining = stateRef.current.lists.filter(
        (list) => list.ListId !== listId
      );
      const nextSelected =
        stateRef.current.selectedListId === listId
          ? remaining.length > 0
            ? remaining[0].ListId
            : null
          : stateRef.current.selectedListId;

      await write(
        () => {
          setLists(remaining);
          setItemsByList((prev) => {
            const next = { ...prev };
            delete next[listId];
            return next;
          });
          setSelectedListId(nextSelected);
          writeStoredSelectedListId(nextSelected);
        },
        () => DeleteList(listId, getRefreshedToken, dataUserId),
        [{ kind: "list.delete", listId }]
      );
    },
    [dataUserId, getRefreshedToken, write]
  );

  const addItem = useCallback(
    async (
      listId: string,
      text: string,
      quantity: string = "",
      note: string = ""
    ) => {
      const trimmed = text.trim();
      if (trimmed === "") {
        return;
      }
      const now = new Date().toISOString();
      const item: ListItem = {
        ListId: listId,
        ItemId: uuidv4(),
        Text: trimmed,
        Checked: false,
        Quantity: quantity.trim(),
        Note: note.trim(),
        SortOrder: nextSortOrder(stateRef.current.itemsByList[listId] ?? []),
        CreatedAt: now,
        UpdatedAt: now,
      };
      await write(
        () => {
          setItemsByList((prev) => ({
            ...prev,
            [listId]: sortListItems([...(prev[listId] ?? []), item]),
          }));
        },
        () => PostListItem(item, getRefreshedToken, dataUserId),
        [{ kind: "item.upsert", item }]
      );
    },
    [dataUserId, getRefreshedToken, write]
  );

  // Shared by toggleItem and updateItem: upsert one changed item.
  const saveItem = useCallback(
    async (listId: string, updated: ListItem) => {
      await write(
        () => {
          setItemsByList((prev) => ({
            ...prev,
            [listId]: sortListItems(
              (prev[listId] ?? []).map((row) =>
                row.ItemId === updated.ItemId ? updated : row
              )
            ),
          }));
        },
        () => PostListItem(updated, getRefreshedToken, dataUserId),
        [{ kind: "item.upsert", item: updated }]
      );
    },
    [dataUserId, getRefreshedToken, write]
  );

  const toggleItem = useCallback(
    async (listId: string, itemId: string) => {
      const existing = (stateRef.current.itemsByList[listId] ?? []).find(
        (row) => row.ItemId === itemId
      );
      if (existing == null) {
        return;
      }
      await saveItem(listId, {
        ...existing,
        Checked: !existing.Checked,
        UpdatedAt: new Date().toISOString(),
      });
    },
    [saveItem]
  );

  const updateItem = useCallback(
    async (listId: string, itemId: string, edit: ListItemEdit) => {
      const existing = (stateRef.current.itemsByList[listId] ?? []).find(
        (row) => row.ItemId === itemId
      );
      if (existing == null) {
        return;
      }
      const updated: ListItem = {
        ...existing,
        ...(edit.Text != null ? { Text: edit.Text.trim() } : {}),
        ...(edit.Quantity != null ? { Quantity: edit.Quantity.trim() } : {}),
        ...(edit.Note != null ? { Note: edit.Note.trim() } : {}),
        UpdatedAt: new Date().toISOString(),
      };
      if (updated.Text === "") {
        return; // Text is required — an empty edit is a no-op, not a delete.
      }
      await saveItem(listId, updated);
    },
    [saveItem]
  );

  const removeItem = useCallback(
    async (listId: string, itemId: string) => {
      await write(
        () => {
          setItemsByList((prev) => ({
            ...prev,
            [listId]: (prev[listId] ?? []).filter(
              (row) => row.ItemId !== itemId
            ),
          }));
        },
        () => DeleteListItem(listId, itemId, getRefreshedToken, dataUserId),
        [{ kind: "item.delete", listId, itemId }]
      );
    },
    [dataUserId, getRefreshedToken, write]
  );

  const deleteChecked = useCallback(
    async (listId: string) => {
      const checkedIds = (stateRef.current.itemsByList[listId] ?? [])
        .filter((row) => row.Checked)
        .map((row) => row.ItemId);
      if (checkedIds.length === 0) {
        return;
      }
      await write(
        () => {
          setItemsByList((prev) => ({
            ...prev,
            [listId]: (prev[listId] ?? []).filter((row) => !row.Checked),
          }));
        },
        () => DeleteListItems(listId, checkedIds, getRefreshedToken, dataUserId),
        [{ kind: "items.delete", listId, itemIds: checkedIds }]
      );
    },
    [dataUserId, getRefreshedToken, write]
  );

  const setAllChecked = useCallback(
    async (listId: string, checked: boolean) => {
      const items = stateRef.current.itemsByList[listId] ?? [];
      // Only the items that actually change state are written.
      const changed = items.filter((row) => row.Checked !== checked);
      if (changed.length === 0) {
        return;
      }
      const now = new Date().toISOString();
      const updated = changed.map((row) => ({
        ...row,
        Checked: checked,
        UpdatedAt: now,
      }));
      const updatedById = new Map(updated.map((row) => [row.ItemId, row]));

      await write(
        () => {
          setItemsByList((prev) => ({
            ...prev,
            [listId]: sortListItems(
              (prev[listId] ?? []).map(
                (row) => updatedById.get(row.ItemId) ?? row
              )
            ),
          }));
        },
        () =>
          Promise.all(
            updated.map((item) =>
              PostListItem(item, getRefreshedToken, dataUserId)
            )
          ),
        updated.map((item) => ({ kind: "item.upsert" as const, item }))
      );
    },
    [dataUserId, getRefreshedToken, write]
  );

  const reorderItems = useCallback(
    async (listId: string, orderedItemIds: string[]) => {
      const current = stateRef.current.itemsByList[listId] ?? [];
      const byId = new Map(current.map((row) => [row.ItemId, row]));
      const ordered = orderedItemIds
        .map((itemId) => byId.get(itemId))
        .filter((row): row is ListItem => row != null);
      if (ordered.length !== current.length) {
        return; // The ids didn't line up with the list; ignore rather than reshuffle.
      }

      // Numbering straight down the displayed order is enough even though the display
      // groups unchecked before checked: the sort reads Checked first, so ascending
      // numbers preserve the intended order inside each group.
      const now = new Date().toISOString();
      const changed: ListItem[] = [];
      const renumbered = ordered.map((row, index) => {
        const sortOrder = (index + 1) * SORT_ORDER_STEP;
        if (row.SortOrder === sortOrder) {
          return row;
        }
        const updated = { ...row, SortOrder: sortOrder, UpdatedAt: now };
        changed.push(updated);
        return updated;
      });

      // Only the items whose position actually moved are written, which for a single
      // drag is the span between where it left and where it landed.
      if (changed.length === 0) {
        return;
      }
      await write(
        () => {
          setItemsByList((prev) => ({
            ...prev,
            [listId]: sortListItems(renumbered),
          }));
        },
        () =>
          Promise.all(
            changed.map((item) =>
              PostListItem(item, getRefreshedToken, dataUserId)
            )
          ),
        changed.map((item) => ({ kind: "item.upsert" as const, item }))
      );
    },
    [dataUserId, getRefreshedToken, write]
  );

  return (
    <ListsContext.Provider
      value={{
        lists,
        itemsByList,
        selectedListId,
        loadState,
        errorMessage,
        apiConfigured,
        demoMode,
        resetDemoData,
        pendingCount,
        syncedCount,
        clearSyncedCount,
        usingCachedData,
        clearError,
        loadLists,
        selectList,
        createList,
        renameList,
        removeList,
        addItem,
        toggleItem,
        updateItem,
        removeItem,
        deleteChecked,
        setAllChecked,
        reorderItems,
      }}
    >
      {props.children}
    </ListsContext.Provider>
  );
};

function useListsContext() {
  const context = useContext(ListsContext);
  if (!context) {
    throw new Error("useListsContext must be used within the Lists provider");
  }
  return context;
}

export { Lists, useListsContext };
